<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isOpen"
        class="modal-overlay"
        :class="{ 'modal-hidden': hidden }"
        @click.self="handleOverlayClick"
      >
        <Transition name="modal-slide">
          <div
            v-if="isOpen"
            class="modal-container"
            :class="containerClass"
            :style="{ maxWidth: maxWidth }"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
          >
            <!-- Modal Header -->
            <div v-if="title || showCloseButton" class="modal-header">
              <h2 v-if="title" :id="titleId" class="modal-title">{{ title }}</h2>
              <button
                v-if="showCloseButton && closable"
                @click="handleClose"
                class="modal-close-button"
                aria-label="Close modal"
              >
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>

            <!-- Modal Body -->
            <div class="modal-body">
              <slot />
            </div>

            <!-- Modal Footer -->
            <div v-if="$slots.footer || showActions" class="modal-footer">
              <slot name="footer">
                <div v-if="showActions" class="modal-actions">
                  <button
                    v-if="cancelText"
                    @click="handleCancel"
                    class="btn-secondary"
                    :disabled="loading"
                  >
                    {{ cancelText }}
                  </button>
                  <button
                    v-if="confirmText"
                    @click="handleConfirm"
                    class="btn-primary"
                    :disabled="loading || confirmDisabled"
                  >
                    <span v-if="loading" class="loading-spinner"></span>
                    <span>{{ confirmText }}</span>
                  </button>
                </div>
              </slot>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'

/**
 * BaseModal Component
 *
 * Reusable modal container with:
 * - Teleport to body for proper z-index handling
 * - Transition animations
 * - Keyboard navigation (ESC to close)
 * - Click outside to close
 * - Customizable header, body, footer
 * - Action buttons with loading states
 * - ARIA accessibility attributes
 *
 * @example
 * <BaseModal
 *   title="Confirm Action"
 *   :is-open="showModal"
 *   confirm-text="Confirm"
 *   cancel-text="Cancel"
 *   @confirm="handleConfirm"
 *   @close="showModal = false"
 * >
 *   <p>Are you sure?</p>
 * </BaseModal>
 */

interface Props {
  /** Whether the modal is open */
  isOpen: boolean
  /** Whether the modal is hidden (preserves state but invisible) */
  hidden?: boolean
  /** Modal title */
  title?: string
  /** Whether the modal can be closed by clicking outside or pressing ESC */
  closable?: boolean
  /** Whether to show the close button */
  showCloseButton?: boolean
  /** Maximum width of the modal */
  maxWidth?: string
  /** Custom CSS class for the container */
  containerClass?: string
  /** Whether to show default action buttons */
  showActions?: boolean
  /** Confirm button text */
  confirmText?: string
  /** Cancel button text */
  cancelText?: string
  /** Whether the confirm button is disabled */
  confirmDisabled?: boolean
  /** Whether actions are loading */
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hidden: false,
  closable: true,
  showCloseButton: true,
  maxWidth: '600px',
  containerClass: '',
  showActions: false,
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmDisabled: false,
  loading: false,
})

const emit = defineEmits<{
  close: []
  confirm: []
  cancel: []
}>()

// Generate unique ID for aria-labelledby
const titleId = computed(() => `modal-title-${Math.random().toString(36).slice(2, 11)}`)

/**
 * Handle close button click
 */
function handleClose(): void {
  if (props.closable) {
    emit('close')
  }
}

/**
 * Handle overlay click
 */
function handleOverlayClick(): void {
  if (props.closable) {
    emit('close')
  }
}

/**
 * Handle confirm button click
 */
function handleConfirm(): void {
  emit('confirm')
}

/**
 * Handle cancel button click
 */
function handleCancel(): void {
  emit('cancel')
  emit('close')
}

/**
 * Handle ESC key press
 */
function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.closable && props.isOpen) {
    handleClose()
  }
}

// Add keyboard listener
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* ============================================================================
   Modal Overlay
   ============================================================================ */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

/* Hidden state - preserves DOM but visually hides modal */
.modal-overlay.modal-hidden {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

/* ============================================================================
   Modal Container
   ============================================================================ */

.modal-container {
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.98) 0%, rgba(26, 47, 63, 0.98) 100%);
  border-radius: 24px;
  box-shadow:
    0 32px 96px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Glowing border effect */
.modal-container::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 24px;
  padding: 2px;
  background: linear-gradient(
    135deg,
    rgba(232, 197, 71, 0.3),
    rgba(232, 197, 71, 0.05),
    rgba(232, 197, 71, 0.3)
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.6;
  pointer-events: none;
}

/* ============================================================================
   Modal Header
   ============================================================================ */

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  z-index: 1;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-close-button {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
}

.modal-close-button:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
}

.modal-close-button .material-symbols-outlined {
  font-size: 1.25rem;
}

/* ============================================================================
   Modal Body
   ============================================================================ */

.modal-body {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
  position: relative;
  z-index: 1;
}

/* Custom scrollbar */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(232, 197, 71, 0.3);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(232, 197, 71, 0.5);
}

/* ============================================================================
   Modal Footer
   ============================================================================ */

.modal-footer {
  padding: 1rem 2rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  z-index: 1;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

/* ============================================================================
   Buttons
   ============================================================================ */

.btn-primary,
.btn-secondary {
  padding: 0.75rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(232, 197, 71, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-secondary:active:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading spinner */
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(13, 31, 45, 0.3);
  border-top-color: #0d1f2d;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================================================
   Transitions
   ============================================================================ */

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-slide-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.modal-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}

/* ============================================================================
   Responsive
   ============================================================================ */

@media (max-width: 640px) {
  .modal-header {
    padding: 1.5rem 1.5rem 1rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .modal-footer {
    padding: 1rem 1.5rem 1.5rem;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
}
</style>
